import InstanceTypeOffering from '../models/instance_type_offering';
import InstanceType from '../models/instance_type';

export const PAGE_SIZE = 25;

export const INITIAL_FILTERS = {
  identifier: '',
  vcpusMin: '',
  vcpusMax: '',
  memoryMinGb: '',
  memoryMaxGb: '',
  gpuRequired: 'any',
};

const MIB_PER_GB = 1024;

const buildApiFilters = filters => {
  const apiFilters = {};

  if (filters.identifier && filters.identifier !== '') {
    apiFilters.identifier = filters.identifier;
  }

  const vcpus = {};
  if (filters.vcpusMin !== '' && filters.vcpusMin != null) {
    vcpus.gte = Number(filters.vcpusMin);
  }
  if (filters.vcpusMax !== '' && filters.vcpusMax != null) {
    vcpus.lte = Number(filters.vcpusMax);
  }
  if (Object.keys(vcpus).length > 0) {
    apiFilters.vcpus = vcpus;
  }

  const memorySize = {};
  if (filters.memoryMinGb !== '' && filters.memoryMinGb != null) {
    memorySize.gte = Number(filters.memoryMinGb) * MIB_PER_GB;
  }
  if (filters.memoryMaxGb !== '' && filters.memoryMaxGb != null) {
    memorySize.lte = Number(filters.memoryMaxGb) * MIB_PER_GB;
  }
  if (Object.keys(memorySize).length > 0) {
    apiFilters.memory_size = memorySize;
  }

  if (filters.gpuRequired === 'yes') {
    apiFilters.gpu_count = { gt: 0 };
  } else if (filters.gpuRequired === 'no') {
    apiFilters.gpu_count = { eq: 0 };
  }

  return apiFilters;
};

export default {
  name: 'instance_types',
  getReducer: () => {
    const initialData = {
      loadingInstanceTypeOfferings: false,
      instanceTypeOfferings: null,
      selectedOffering: null,
      recommendedOfferings: null,
      availabilityZoneId: undefined,
      filters: INITIAL_FILTERS,
      page: 1,
      meta: null,
    };
    // eslint-disable-next-line no-unused-vars
    return (state = initialData, { type, payload }) => {
      if (type === 'FETCH_INSTANCE_TYPE_OFFERINGS') {
        return {
          ...state,
          loadingInstanceTypeOfferings: true,
          availabilityZoneId: payload.availabilityZoneId,
          filters: payload.filters,
          page: payload.page,
        };
      }
      if (type === 'FETCH_INSTANCE_TYPE_OFFERINGS_SUCCESS') {
        return {
          ...state,
          loadingInstanceTypeOfferings: false,
          instanceTypeOfferings: payload.result,
          meta: payload.meta,
        };
      }
      if (type === 'CHANGE_INSTANCE_TYPE_OFFERINGS_PAGE') {
        return { ...state, page: payload.page };
      }
      if (type === 'CHANGE_INSTANCE_TYPE_OFFERINGS_FILTER') {
        return {
          ...state,
          filters: { ...state.filters, [payload.name]: payload.value },
          page: 1,
        };
      }
      if (type === 'RESET_INSTANCE_TYPE_OFFERINGS_FILTERS') {
        return { ...state, filters: INITIAL_FILTERS, page: 1 };
      }
      if (type === 'SET_SELECTED_OFFERING') {
        return { ...state, selectedOffering: payload };
      }
      if (type === 'SET_RECOMMENDED_OFFERINGS') {
        return { ...state, recommendedOfferings: payload };
      }

      if (type === 'RESET_ACTIVE_FACILITY') {
        return {
          ...state,
          instanceTypeOfferings: null,
          selectedOffering: null,
          recommendedOfferings: null,
          availabilityZoneId: undefined,
          filters: INITIAL_FILTERS,
          page: 1,
          meta: null,
        };
      }

      return state;
    };
  },

  // Two backing resources behind one filter UI:
  //   AZ set  → InstanceTypeOffering (per-AZ/account availability — template picks
  //             only show what the account can actually launch).
  //   AZ null → InstanceType (global AWS catalog — for admin flows that set
  //             catalog-wide flags like `recommended`).
  // Both endpoints accept the same filter shape via buildApiFilters.
  doFetchInstanceTypeOfferings: availabilityZoneId => async ({
    dispatch,
    getState,
    store,
  }) => {
    const state = getState().instance_types;
    const isAzChange = state.availabilityZoneId !== availabilityZoneId;
    const filters = isAzChange ? INITIAL_FILTERS : state.filters;
    const page = isAzChange ? 1 : state.page || 1;

    dispatch({
      type: 'FETCH_INSTANCE_TYPE_OFFERINGS',
      payload: { availabilityZoneId, filters, page },
    });

    const relation =
      availabilityZoneId === null
        ? InstanceType.where({ ...buildApiFilters(filters), aws: true })
            .order({ recommended_priority: 'asc' })
            .order({ identifier: 'asc' })
            .per(PAGE_SIZE)
            .page(page)
            .stats({ total: 'count' })
        : InstanceTypeOffering.where({
            availability_zone_id: availabilityZoneId,
          })
            .where(buildApiFilters(filters))
            .includes(['instance_type'])
            .order({ recommended_priority: 'asc' })
            .order({ identifier: 'asc' })
            .per(PAGE_SIZE)
            .page(page)
            .stats({ total: 'count' });

    const response = await relation.all();

    if (
      response.data.length === 0 &&
      response.meta?.stats?.total?.count > 0
    ) {
      store.doChangeInstanceTypeOfferingsPage(1);
      return;
    }

    // Normalize to a uniform { id, instanceType, recommended } shape so the
    // modal doesn't care which endpoint backed the response. For the global
    // catalog the recommended flag lives on the InstanceType itself.
    const result =
      availabilityZoneId === null
        ? response.data.map(it => ({
            id: it.id,
            instanceType: it,
            recommended: it.recommended,
          }))
        : response.data;

    dispatch({
      type: 'FETCH_INSTANCE_TYPE_OFFERINGS_SUCCESS',
      payload: { result, meta: response.meta },
    });
  },

  doChangeInstanceTypeOfferingsPage: page => async ({
    dispatch,
    getState,
    store,
  }) => {
    dispatch({
      type: 'CHANGE_INSTANCE_TYPE_OFFERINGS_PAGE',
      payload: { page },
    });
    const { availabilityZoneId } = getState().instance_types;
    if (availabilityZoneId !== undefined) {
      store.doFetchInstanceTypeOfferings(availabilityZoneId);
    }
  },

  doChangeInstanceTypeOfferingsFilter: (name, value) => async ({
    dispatch,
    getState,
    store,
  }) => {
    dispatch({
      type: 'CHANGE_INSTANCE_TYPE_OFFERINGS_FILTER',
      payload: { name, value },
    });
    const { availabilityZoneId } = getState().instance_types;
    if (availabilityZoneId !== undefined) {
      store.doFetchInstanceTypeOfferings(availabilityZoneId);
    }
  },

  doResetInstanceTypeOfferingsFilters: () => async ({
    dispatch,
    getState,
    store,
  }) => {
    dispatch({ type: 'RESET_INSTANCE_TYPE_OFFERINGS_FILTERS' });
    const { availabilityZoneId } = getState().instance_types;
    if (availabilityZoneId !== undefined) {
      store.doFetchInstanceTypeOfferings(availabilityZoneId);
    }
  },

  doFetchSelectedOffering: instanceTypeId => async ({ dispatch, getState }) => {
    const { availabilityZoneId } = getState().instance_types;
    if (!instanceTypeId || !availabilityZoneId) {
      dispatch({ type: 'SET_SELECTED_OFFERING', payload: null });
      return;
    }
    const response = await InstanceTypeOffering.where({
      availability_zone_id: availabilityZoneId,
      instance_type_id: instanceTypeId,
    })
      .includes(['instance_type'])
      .all();
    dispatch({
      type: 'SET_SELECTED_OFFERING',
      payload: response.data[0] || null,
    });
  },

  doFetchRecommendedOfferings: () => async ({ dispatch, getState }) => {
    const { availabilityZoneId } = getState().instance_types;
    if (!availabilityZoneId) {
      dispatch({ type: 'SET_RECOMMENDED_OFFERINGS', payload: [] });
      return;
    }
    const recommended = await InstanceType.where({ recommended: true })
      .order({ recommended_priority: 'asc' })
      .per(50)
      .all();
    const ids = recommended.data.map(it => it.id);
    if (ids.length === 0) {
      dispatch({ type: 'SET_RECOMMENDED_OFFERINGS', payload: [] });
      return;
    }
    const offerings = await InstanceTypeOffering.where({
      availability_zone_id: availabilityZoneId,
      instance_type_id: ids,
    })
      .includes(['instance_type'])
      .per(ids.length)
      .all();
    const byInstanceTypeId = Object.fromEntries(
      offerings.data.map(o => [o.instanceType?.id, o]),
    );
    const ordered = ids.map(id => byInstanceTypeId[id]).filter(Boolean);
    dispatch({ type: 'SET_RECOMMENDED_OFFERINGS', payload: ordered });
  },

  doFetchInstanceTypeForFacility: () => async ({ getState, store }) => {
    const state = getState();

    const facility = store.selectActiveFacility(state);
    const scope = InstanceType.where({
      facility_id: facility.id,
    }).order({ identifier: 'asc' });

    const response = await scope.all();

    return response.data;
  },

  selectInstanceTypeOfferings: state =>
    state.instance_types.instanceTypeOfferings,

  selectInstanceTypeOfferingsSelectedOffering: state =>
    state.instance_types.selectedOffering,

  selectInstanceTypeOfferingsRecommendedOfferings: state =>
    state.instance_types.recommendedOfferings,

  selectLoadingInstanceTypeOfferings: state =>
    state.instance_types.loadingInstanceTypeOfferings,

  selectInstanceTypeOfferingsFilters: state => state.instance_types.filters,

  selectInstanceTypeOfferingsPage: state => state.instance_types.page || 1,

  selectInstanceTypeOfferingsMeta: state => state.instance_types.meta,

  selectInstanceTypeOfferingsTotalCount: state =>
    state.instance_types.meta?.stats?.total?.count || 0,

  selectInstanceTypeOfferingsPageCount: state =>
    Math.ceil(
      (state.instance_types.meta?.stats?.total?.count || 0) / PAGE_SIZE,
    ),
};
